import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, User, ShoppingBag, Heart, Globe, ChevronDown, Palette, CalendarHeart, Phone, MessageCircle, Coins, ArrowRight, ArrowLeft } from "lucide-react";
import { useApp } from "@/context";
import { BRAND } from "@/brand";
import NotificationsBell from "@/components/NotificationsBell";
import LiveActivityTicker from "@/components/LiveActivityTicker";
import { registerSW } from "@/push";
import { registerNativePush } from "@/lib/nativePush";
import http from "@/api";
import {
  CONTACT_PHONE_E164, CONTACT_PHONE_DISPLAY,
  CONTACT_WHATSAPP_DISPLAY, waLink,
} from "@/lib/contact";

const LOGO_MASK_STYLE = {
  WebkitMask: `url(${BRAND.logoTransparent}) no-repeat center / contain`,
  mask: `url(${BRAND.logoTransparent}) no-repeat center / contain`,
};

const SOCIAL_PLATFORMS = [
  { key: "instagram", icon: "fa-instagram",  base: (u) => `https://instagram.com/${u}` },
  { key: "tiktok",    icon: "fa-tiktok",     base: (u) => `https://tiktok.com/@${u}` },
  { key: "snapchat",  icon: "fa-snapchat",   base: (u) => `https://snapchat.com/add/${u}` },
  { key: "x",         icon: "fa-x-twitter",  base: (u) => `https://x.com/${u}` },
  { key: "youtube",   icon: "fa-youtube",    base: (u) => `https://youtube.com/@${u}` },
  { key: "facebook",  icon: "fa-facebook-f", base: (u) => `https://facebook.com/${u}` },
];

function SocialLinksRow() {
  const [links, setLinks] = useState(null);
  useEffect(() => {
    http.get("/social-links").then(({ data }) => setLinks(data.links || {})).catch(() => setLinks({}));
  }, []);
  if (!links) return null;
  const rows = SOCIAL_PLATFORMS
    .map((p) => {
      const raw = (links[p.key] || "").trim();
      if (!raw) return null;
      const href = /^https?:\/\//i.test(raw) ? raw : p.base(raw.replace(/^@/, ""));
      return { ...p, href };
    })
    .filter(Boolean);
  if (!rows.length) return null;
  return (
    <div className="flex justify-center gap-3 mb-3" data-testid="footer-social-row">
      {rows.map((p) => (
        <a key={p.key} href={p.href} target="_blank" rel="noopener noreferrer"
           className="w-9 h-9 rounded-full flex items-center justify-center transition-transform hover:scale-110"
           style={{ background: "rgba(176,141,77,0.12)", color: "#8a6c33", border: "1px solid rgba(176,141,77,0.3)" }}
           aria-label={p.key} data-testid={`footer-social-${p.key}`}>
          <i className={`fa-brands ${p.icon} text-[15px]`} />
        </a>
      ))}
    </div>
  );
}

import CustomerServiceWidget from "@/components/CustomerServiceWidget";
import PushEnableBanner from "@/components/PushEnableBanner";
import PhoneGateModal from "@/components/PhoneGateModal";

/* زر تنقل سفلي — التفعيل فور ملامسة الإصبع (pointerdown) كالتطبيقات الأصلية.
   إزالة حارس الـ500ms القديم: كان يبتلع الضغطة الثانية الحقيقية للمستخدم.
   الآن كل pointerdown يُنفّذ فوراً، وclick المصطنع لنفس اللمسة فقط يُهمل،
   والتكرار آمن لأن activate يتجاهل الوجهة الحالية بفحص حي. */
function NavBtn({ active, onActivate, children, testId }) {
  const lastPointer = useRef(0);
  const start = useRef(null);
  const act = () => {
    try { if (navigator.vibrate) navigator.vibrate(8); } catch (_) { /* */ }
    // درع ضد "النقرة الشبح": click المصطنع بعد التنقل يسقط على الصفحة الجديدة
    // بنفس الإحداثيات فيضغط عنصراً لم يقصده المستخدم — نبتلعه مرة واحدة.
    const shield = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
    document.addEventListener("click", shield, { capture: true, once: true });
    setTimeout(() => document.removeEventListener("click", shield, { capture: true }), 450);
    onActivate();
  };
  return (
    <button
      type="button"
      className={`nav-item relative ${active ? "active" : ""}`}
      data-testid={testId}
      onPointerDown={(e) => {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        start.current = { x: e.clientX, y: e.clientY, t: Date.now() };
      }}
      onPointerUp={(e) => {
        const s = start.current;
        start.current = null;
        if (!s) return;
        // تنقّل فقط إذا كانت ضغطة مقصودة: بلا انزلاق (>12px) وبلا ضغط مطوّل
        if (Math.hypot(e.clientX - s.x, e.clientY - s.y) > 12) return;
        if (Date.now() - s.t > 700) return;
        // حارس هندسي: الإصبع يجب أن يكون داخل حدود الزر الفعلية لحظة الرفع
        // — لو انزاح الشريط تحت الإصبع أثناء اللمسة (خلل iOS) تُهمل الضغطة
        const r = e.currentTarget.getBoundingClientRect();
        if (e.clientX < r.left - 6 || e.clientX > r.right + 6 ||
            e.clientY < r.top - 6 || e.clientY > r.bottom + 6) return;
        // حارس الموضع: ضغطة التبويب لازم تكون قرب أسفل الشاشة المرئية فعلاً —
        // لو طفا الشريط فوق المحتوى (انزياح iOS بعد الكيبورد) لا يخطف ضغطات الشراء
        if (e.pointerType !== "mouse") {
          const vv = window.visualViewport;
          const visualBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
          if (visualBottom - e.clientY > 130) return;
        }
        lastPointer.current = Date.now();
        act();
      }}
      onPointerCancel={() => { start.current = null; }}
      onClick={() => {
        // click المصطنع يتبع pointerup لنفس اللمسة خلال <700ms — لا تكرار.
        // click بلا pointer events (لوحة مفاتيح/قارئ شاشة) يعمل طبيعياً.
        if (Date.now() - lastPointer.current < 700) return;
        act();
      }}
    >
      {children}
    </button>
  );
}

export default function Layout({ children }) {
  const { t, lang, setLang, currency, setCurrency, currencies, country, setCountry, countries, cart, user, favorites, customLogo, branchId, setBranchId } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);
  const [csOpen, setCsOpen] = useState(false);
  const [csUnread, setCsUnread] = useState(0);
  const [csOrderId, setCsOrderId] = useState("");
  useEffect(() => {
    const h = (e) => { setCsOrderId(e.detail?.orderId || ""); setCsOpen(true); setCsUnread(0); };
    window.addEventListener("rg:open-chat", h);
    return () => window.removeEventListener("rg:open-chat", h);
  }, []);
  const [pendingNav, setPendingNav] = useState(null);
  const location = useLocation();
  const nav = useNavigate();

  // React Router v7 يُبقي الصفحة القديمة أثناء تحميل الصفحة الجديدة (startTransition)
  // — نمسح مؤشر "قيد التنقل" فور اكتمال الانتقال فعلياً
  useEffect(() => { setPendingNav(null); }, [location.pathname]);
  // مهلة أمان: لو فشل تحميل chunk الصفحة لا يبقى الشريط ظاهراً للأبد
  useEffect(() => {
    if (!pendingNav) return undefined;
    const t = setTimeout(() => setPendingNav(null), 4000);
    return () => clearTimeout(t);
  }, [pendingNav]);

  // Register the service worker on mount so the browser considers the site
  // installable and remembers the scope for push notifications.
  useEffect(() => { registerSW().catch(() => {}); }, []);
  useEffect(() => { if (user) registerNativePush(); }, [user]);

  // Unread support-chat badge (bottom nav) — light poll, logged-in users only.
  useEffect(() => {
    if (!user) { setCsUnread(0); return; }
    const fetchUnread = () => http.get("/support/unread")
      .then(({ data }) => setCsUnread(data.unread || 0)).catch(() => {});
    fetchUnread();
    const id = setInterval(fetchUnread, 45000);
    return () => clearInterval(id);
  }, [user]);

  const cartCount = cart.reduce((a, i) => a + i.quantity, 0);
  const isApp = !location.pathname.startsWith("/admin") && !location.pathname.startsWith("/staff") && !location.pathname.startsWith("/intake") && !location.pathname.startsWith("/ops") && !location.pathname.startsWith("/purchases") && !location.pathname.startsWith("/buy");
  const isOpsScreen = location.pathname.startsWith("/ops") || location.pathname.startsWith("/purchases") || location.pathname.startsWith("/buy");
  const isStaffUser = user && ["employee","staff","driver","cashier","receiver","admin","branch_admin","country_admin","super_admin"].includes(user.role);

  // صفحات الدفع: أولوية كاملة لأزرار إتمام الشراء — لا شريط تنقل ولا زر دعم فوقها
  const isCheckoutFlow = location.pathname.startsWith("/checkout") || location.pathname.startsWith("/pay");
  // لوحة المفاتيح المفتوحة (جوال): الشريط الثابت يُخفى حتى لا يغطي حقول الإدخال
  const [kbOpen, setKbOpen] = useState(false);
  useEffect(() => {
    const isField = (el) => el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" ||
      el.tagName === "SELECT" || el.isContentEditable);
    const small = () => window.matchMedia("(max-width: 1023px)").matches;
    const onIn = (e) => { if (small() && isField(e.target)) setKbOpen(true); };
    const onOut = () => setTimeout(() => {
      if (!isField(document.activeElement)) {
        setKbOpen(false);
        // إعادة تثبيت العناصر الثابتة: iOS يترك الصفحة منزاحة بعد إغلاق الكيبورد
        // فيطفو الشريط السفلي فوق المحتوى — نكزة scroll تعيده لمكانه الصحيح
        window.scrollTo(window.scrollX, window.scrollY);
      }
    }, 80);
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => {
      document.removeEventListener("focusin", onIn);
      document.removeEventListener("focusout", onOut);
    };
  }, []);

  // إصلاح انزياح iOS: عند عودة الـvisual viewport لحجمه الكامل (إغلاق كيبورد/رجوع
  // من الخلفية) نعيد تثبيت position:fixed حتى لا يبقى الشريط طافياً فوق المحتوى
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const reanchor = () => {
      if (Math.abs(vv.height + vv.offsetTop - window.innerHeight) < 2) {
        window.scrollTo(window.scrollX, window.scrollY);
      }
    };
    vv.addEventListener("resize", reanchor);
    return () => vv.removeEventListener("resize", reanchor);
  }, []);


  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {/* شريط تقدم أعلى الشاشة — استجابة مرئية فورية لأي ضغطة تنقل */}
      {pendingNav && <div className="nav-progress" data-testid="nav-progress" />}
      {/* Top header (sticky glass) — hidden on isolated ops work screens */}
      {!isOpsScreen && (
      <header className="glass sticky top-0 z-40" data-testid="app-header"
              style={{ paddingTop: "env(safe-area-inset-top)", boxShadow: "0 2px 14px rgba(0,0,0,0.07)" }}>
        <div className="max-w-6xl mx-auto px-3 sm:px-6 h-14 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 sm:gap-1.5 min-w-0 shrink">
            {/* زر رجوع عام — بكل الصفحات عدا الرئيسية (طلب يونيو 2026) */}
            {location.pathname !== "/" && (
              <button
                onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
                className="p-1.5 sm:p-2 rounded-full hover:bg-black/5 shrink-0 inline-flex"
                data-testid="header-back-btn"
                aria-label={lang === "ar" ? "رجوع" : "Back"}
                title={lang === "ar" ? "رجوع" : "Back"}>
                {lang === "ar"
                  ? <ArrowRight size={21} className="sm:w-[23px] sm:h-[23px]" />
                  : <ArrowLeft size={21} className="sm:w-[23px] sm:h-[23px]" />}
              </button>
            )}
            {/* أيقونة الحساب — أول عنصر بالهيدر كالتصميم المعتمد */}
            <Link to={user ? "/profile" : "/login"}
                  className="p-1.5 sm:p-2 rounded-full hover:bg-black/5 shrink-0 inline-flex"
                  data-testid="header-profile-btn"
                  aria-label={user ? "Profile" : "Login"}
                  title={lang === "ar" ? (user ? "حسابي" : "تسجيل الدخول") : (user ? "Account" : "Login")}>
              <User size={21} className="sm:w-[23px] sm:h-[23px]" />
            </Link>
            <Link to="/" className="flex items-center gap-2 min-w-0 shrink" data-testid="logo-link">
              {customLogo ? (
                <img src={customLogo} alt={BRAND.name}
                     className="h-8 sm:h-10 w-auto shrink-0 object-contain" data-testid="header-logo" />
              ) : (
                <span role="img" aria-label={`${BRAND.name} — Luxury Flowers & Gifts`}
                      className="brand-logo-mask h-8 sm:h-10 shrink-0" style={LOGO_MASK_STYLE} data-testid="header-logo" />
              )}
            </Link>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Staff task board — visible only to employees/admins */}
            {isStaffUser && (
              <Link to="/ops"
                    className="chip !px-2.5 !py-1.5 !gap-1 text-[11px] sm:text-sm font-semibold"
                    style={{ background: "var(--primary)", color: "#fff", borderColor: "var(--primary)" }}
                    data-testid="header-staff-btn">
                🛠️ <span>{lang === "ar" ? "الطاقم" : "Staff"}</span>
              </Link>
            )}
            {/* Lang / Currency */}
            <div className="relative">
              <button
                className="chip !px-2 !py-1.5 !gap-1 text-[11px] sm:text-sm"
                onClick={() => setMenuOpen((v) => !v)}
                data-testid="lang-currency-btn"
              >
                <Globe size={13} className="shrink-0" />
                {(() => {
                  const c = countries.find((x) => x.code === country);
                  return c ? <span className="leading-none">{c.flag}</span> : null;
                })()}
                <span>{lang === "ar" ? "عربي" : "EN"}</span>
                <span className="opacity-60">·</span>
                <span>{country === "SA" ? "SAR" : currency}</span>
                <ChevronDown size={12} className="shrink-0" />
              </button>
              {menuOpen && (
                <div
                  className="absolute mt-2 p-3 bg-white rounded-2xl shadow-2xl border w-64 z-50"
                  style={{ [lang === "ar" ? "left" : "right"]: 0 }}
                  onMouseLeave={() => setMenuOpen(false)}
                  data-testid="lang-currency-menu"
                >
                  {countries.length > 0 && (
                    <>
                      <div className="text-xs uppercase tracking-widest opacity-60 mb-2">
                        {lang === "ar" ? "الدولة" : "Country"}
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        {countries.map((c) => (
                          <button
                            key={c.code}
                            className={`chip justify-center ${country === c.code ? "active" : ""}`}
                            onClick={() => {
                              if (c.code !== country) { setBranchId(""); }
                              setCountry(c.code);
                              if (c.code === "SA") {
                                setMenuOpen(false);
                                // افتح محدد الفروع السعودية (3 فروع إقليمية) فوراً
                                setTimeout(() => window.dispatchEvent(new CustomEvent("rg:open-location")), 150);
                              }
                            }}
                            data-testid={`country-${c.code}-btn`}
                          >
                            <span className="text-base leading-none">{c.flag}</span>
                            <span className="text-xs">{lang === "ar" ? c.ar : c.en}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  <div className="text-xs uppercase tracking-widest opacity-60 mb-2">{t("language")}</div>
                  <div className="flex gap-2 mb-3">
                    {["ar", "en"].map((l) => (
                      <button
                        key={l}
                        className={`chip flex-1 justify-center ${lang === l ? "active" : ""}`}
                        onClick={() => { setLang(l); }}
                        data-testid={`lang-${l}-btn`}
                      >
                        {l === "ar" ? "العربية" : "English"}
                      </button>
                    ))}
                  </div>
                  <div className="text-xs uppercase tracking-widest opacity-60 mb-2">{t("currency")}</div>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.keys(currencies).map((c) => (
                      <button
                        key={c}
                        className={`chip justify-center ${currency === c ? "active" : ""}`}
                        onClick={() => setCurrency(c)}
                        data-testid={`currency-${c}-btn`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>

                </div>
              )}
            </div>

            <Link to="/favorites" className="relative p-1.5 sm:p-2 rounded-full hover:bg-black/5 inline-flex" data-testid="header-favorites-btn"
                  aria-label="Favorites" title={lang === "ar" ? "المفضّلة" : "Favorites"}>
              <Heart size={20} className="sm:w-[22px] sm:h-[22px]" style={favorites.size > 0 ? { color: "#e11d48" } : undefined}
                     fill={favorites.size > 0 ? "#e11d48" : "none"} />
              {favorites.size > 0 && (
                <span className="absolute -top-1 -right-1 text-white text-[10px] rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
                      style={{ background: "#e11d48" }}>
                  {favorites.size}
                </span>
              )}
            </Link>

            <NotificationsBell />

            <Link to="/cart" className="relative p-2 rounded-full hover:bg-black/5" data-testid="cart-icon">
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center"
                  style={{ background: "var(--secondary)", color: "#000" }}
                >
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </header>
      )}
      {/* Live activity ticker — social proof for customer pages */}
      {isApp && <LiveActivityTicker />}

      <main className={isApp ? "pb-safe" : ""}>{children}</main>

      {/* Slim info-links footer — visible on every customer page (above the
          bottom nav so it's reachable but not intrusive). */}
      {isApp && (
        <footer className="px-4 pb-[76px] pt-6 text-center text-[12px] opacity-80" data-testid="layout-footer">
          {customLogo ? (
            <img src={customLogo} alt={BRAND.name} className="h-9 w-auto mb-3 object-contain" style={{ marginInline: "auto", display: "block" }} data-testid="footer-logo" />
          ) : (
            <span role="img" aria-label={BRAND.name} className="brand-logo-mask h-8 mb-3 block" style={{ ...LOGO_MASK_STYLE, marginInline: "auto", width: "2.25rem" }} data-testid="footer-logo" />
          )}
          {/* Social media icons — admin-configurable from settings */}
          <SocialLinksRow />
          {/* Direct contact row — phone + WhatsApp on every page */}
          <div className="flex justify-center gap-2 mb-3" data-testid="footer-contact-row">
            <a href={`tel:${CONTACT_PHONE_E164}`}
               className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-900 border border-emerald-200 hover:bg-emerald-100 transition-colors"
               data-testid="footer-call-btn">
              <Phone size={13} />
              <span className="font-mono text-[11px]" dir="ltr">{CONTACT_PHONE_DISPLAY}</span>
            </a>
            <a href={waLink(lang)} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white hover:opacity-90 transition-opacity"
               style={{ background: "#25D366" }}
               data-testid="footer-wa-btn">
              <MessageCircle size={13} />
              <span className="font-mono text-[11px]" dir="ltr">{CONTACT_WHATSAPP_DISPLAY}</span>
            </a>
          </div>
          {/* Occasion SEO landing pages — internal links help Google discover them */}
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 mb-3" data-testid="footer-occasions-row">
            <span className="font-semibold opacity-90">{lang === "ar" ? "تسوق حسب المناسبة:" : "Shop by occasion:"}</span>
            <Link to="/occasion/عيد-الأم" className="hover:underline hover:opacity-100">{lang === "ar" ? "عيد الأم" : "Mother's Day"}</Link>
            <span className="opacity-40">·</span>
            <Link to="/occasion/عيد-ميلاد" className="hover:underline hover:opacity-100">{lang === "ar" ? "عيد ميلاد" : "Birthday"}</Link>
            <span className="opacity-40">·</span>
            <Link to="/occasion/زواج" className="hover:underline hover:opacity-100">{lang === "ar" ? "زواج" : "Wedding"}</Link>
            <span className="opacity-40">·</span>
            <Link to="/occasion/حب-ورومانسية" className="hover:underline hover:opacity-100">{lang === "ar" ? "حب ورومانسية" : "Romance"}</Link>
            <span className="opacity-40">·</span>
            <Link to="/occasion/تخرج" className="hover:underline hover:opacity-100">{lang === "ar" ? "تخرج" : "Graduation"}</Link>
            <span className="opacity-40">·</span>
            <Link to="/occasion/مولود-جديد" className="hover:underline hover:opacity-100">{lang === "ar" ? "مولود جديد" : "Newborn"}</Link>
          </div>
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 mb-1.5">
            <Link to="/info/about"   className="hover:underline hover:opacity-100" data-testid="footer-about">
              {lang === "ar" ? "من نحن" : "About"}
            </Link>
            <span className="opacity-40">·</span>
            <Link to="/info/faq"     className="hover:underline hover:opacity-100" data-testid="footer-faq">
              {lang === "ar" ? "الأسئلة الشائعة" : "FAQ"}
            </Link>
            <span className="opacity-40">·</span>
            <Link to="/info/contact" className="hover:underline hover:opacity-100" data-testid="footer-contact">
              {lang === "ar" ? "تواصل معنا" : "Contact"}
            </Link>
            <span className="opacity-40">·</span>
            <Link to="/info/terms"   className="hover:underline hover:opacity-100" data-testid="footer-terms">
              {lang === "ar" ? "الشروط" : "Terms"}
            </Link>
            <span className="opacity-40">·</span>
            <Link to="/info/privacy" className="hover:underline hover:opacity-100" data-testid="footer-privacy">
              {lang === "ar" ? "الخصوصية" : "Privacy"}
            </Link>
            <span className="opacity-40">·</span>
            <Link to="/info/refund"  className="hover:underline hover:opacity-100" data-testid="footer-refund">
              {lang === "ar" ? "الاسترجاع" : "Refunds"}
            </Link>
          </div>
          <div className="opacity-60">© {new Date().getFullYear()} RARE GROUP · {lang === "ar" ? "قطر" : "Qatar"}</div>
        </footer>
      )}

      {/* Bottom nav (mobile-first PWA) — Cart is now 3rd (center, prominent)
           with live count badge so the user always has a clear path to checkout. */}
      {isApp && !isCheckoutFlow && (
        <nav
          className="fixed bottom-0 inset-x-0 z-40 glass"
          style={{ borderTop: "1px solid var(--border)", boxShadow: "0 -4px 18px rgba(0,0,0,0.10)",
                   paddingBottom: "env(safe-area-inset-bottom, 0px)",
                   display: kbOpen ? "none" : undefined }}
          data-testid="bottom-nav"
        >
          {/* الأزرار محصورة في صف 56px فقط — شريحة منطقة الأمان تحتها خلفية صامتة
              غير قابلة للنقر (إصلاح جذري: كانت تُحسب مرتين فتتضخم مساحة لمس الأزرار) */}
          <div className="max-w-6xl mx-auto px-2 flex items-stretch"
               style={{ height: "var(--bnav-h)" }}>
            {[
              { to: "/", icon: Home, label: lang === "ar" ? "الرئيسية" : "Home", testId: "nav-home" },
              { to: "/cart", icon: ShoppingBag, label: lang === "ar" ? "السلة" : "Cart", testId: "nav-cart", badge: cartCount },
              { to: "/occasions", icon: CalendarHeart, label: lang === "ar" ? "مناسباتي" : "My Occasions", testId: "nav-occasions" },
              { to: "/earn", icon: Coins, label: lang === "ar" ? "اربح مع RG" : "Earn with RG", testId: "nav-earn" },
              { to: "/profile", icon: User, label: lang === "ar" ? "حسابي" : "Account", testId: "nav-profile" },
            ].map(({ to, icon: Icon, label, testId, badge, small }) => {
              const routeActive = to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
              // إبراز فوري للتبويب المضغوط حتى أثناء تحميل الصفحة الجديدة
              const active = pendingNav ? to === pendingNav : routeActive;
              const activate = () => {
                // فحص حي من المتصفح مباشرة — closure الـReact قد يحمل مساراً قديماً
                // أثناء عرض الصفحة الجديدة فيبتلع الضغطة (سبب «اضغط مرتين وثلاثاً»)
                if (window.location.pathname === to) return;
                setPendingNav(to); nav(to);
              };
              return (
                <NavBtn key={to} active={active} onActivate={activate} testId={testId}>
                  <div className="relative">
                    <Icon size={20} strokeWidth={1.8} />
                    {badge > 0 && (
                      <span className="absolute -top-1.5 -right-2 text-[10px] font-bold rounded-full px-1 py-0 min-w-[18px] h-[18px] flex items-center justify-center"
                            style={{ background: "var(--secondary)", color: "#111" }}>
                        {badge > 99 ? "99+" : badge}
                      </span>
                    )}
                  </div>
                  <span className={small ? "text-[9.5px] tracking-tight" : ""}>{label}</span>
                </NavBtn>
              );
            })}
          </div>
        </nav>
      )}
      {isApp && !isCheckoutFlow && !csOpen && (
        <button onClick={() => { setCsOpen((o) => !o); if (!csOpen) setCsUnread(0); }}
                className="fixed z-40 w-11 h-11 rounded-full flex items-center justify-center text-white shadow-lg active:scale-90 transition-transform"
                style={{ bottom: "calc(var(--bnav-total) + 14px)", insetInlineEnd: 14, background: "#25D366" }}
                data-testid="support-fab" aria-label="support chat">
          <MessageCircle size={20} />
          {csUnread > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {csUnread > 99 ? "99+" : csUnread}
            </span>
          )}
        </button>
      )}
      {isApp && (
        <CustomerServiceWidget open={csOpen} onClose={() => { setCsOpen(false); setCsOrderId(""); }} onRead={() => setCsUnread(0)} orderId={csOrderId} />
      )}
      {isApp && <PushEnableBanner />}
      {/* بوابة رقم الجوال الإلزامية — تظهر لأي حساب مسجَّل دخول بلا رقم جوال */}
      <PhoneGateModal />
    </div>
  );
}
